import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { S3Client, PutObjectCommand } from '@aws-sdk/client-s3';

import { customerApi } from '../services/customerApi';
import { useI18n } from '../i18n';
import { RADIUS_DEFAULT_M } from '../components/map';

/**
 * One Reco conversation, owned above the router.
 *
 * The intake chat has two presentations — a floating widget on the landing page
 * and a full page at /reco — and they used to be two independent mounts, so
 * navigating between them threw the conversation away. The state lives here
 * instead, above <Routes>, and both presentations read the same session.
 *
 * Nothing is serialised. Both mounts share one JS heap, which is what lets the
 * File[] of a pending upload and an in-flight upload promise survive a
 * navigation — neither would survive a trip through sessionStorage. The cost is
 * that a full page reload still starts over, which is the intended trade.
 *
 * What is deliberately NOT here: `peek`, `minimized`, hover timers and the
 * scroll anchor. Those describe how one presentation is drawn, not where the
 * conversation has got to, and the full-page variant has no use for them.
 */

const UPLOAD_CONCURRENCY = 4;
const ACCEPTED_EXTENSIONS = /\.(jpe?g|png|heic|heif|tiff?|cr2|cr3|nef|arw|dng|raw|webp)$/i;

export type Phase =
  | 'intake' | 'connecting' | 'vexcel-confirm'
  /** Preview flow only: sample imagery on screen, awaiting go/no-go. */
  | 'imagery-preview'
  /** Preview flow only: they declined, and we are asking why. */
  | 'cancel-reason'
  | 'email-capture' | 'si-relay' | 'done';

export interface Message {
  role: 'user' | 'assistant' | 'system';
  content: string;
  files?: string[];
  /** A settled reconstruction extent, kept in the transcript for context. */
  extent?: { lat: number; lon: number; radiusM: number; address: string };
  /** Approved sample imagery, kept in the transcript the way an extent is. */
  imageToken?: string;
  imageViews?: string[];
  /**
   * This turn is part of the intake dialogue: something the user typed or chose,
   * or a reply the intake model produced. Everything else in the transcript is
   * narration the app writes around the conversation, and only dialogue is sent
   * back to the model.
   *
   * Narration comes in both voices, and both mislead.
   *
   * In Reco's voice — "Locating that address…", "I found it — … Select the area
   * you want to reconstruct", the coverage verdict — the model read them as its
   * own prior words and imitated them, writing the sentence out as prose instead
   * of returning the structured worldMapAddress that triggers the geocode. The
   * bubble appeared, no geocode ran, no map arrived.
   *
   * In the user's voice — "I have selected the map area.", "Previews look
   * good.", "Cancelled" — they are acknowledgements the app writes on the user's
   * behalf. Excluding only Reco's half left these behind with nothing around
   * them, so after a failed coverage check the model saw a user who had picked
   * an area and then asked for an address again, concluded the intake was nearly
   * done, and asked for an email.
   *
   * So the rule is by authorship, not by role: the user typed or chose it, or
   * the model said it. Tagging those two rather than every narration keeps it
   * closed — anything added to the transcript later is excluded by default.
   */
  dialogue?: boolean;
  /**
   * A line under everything before it: the previous attempt is over and the next
   * turn starts a new request.
   *
   * The intake model is sent only the dialogue *after* the last of these. Without
   * it the transcript is the state — the model re-derives what to do by reading
   * the whole history, so a location that failed its coverage check was still
   * sitting there as the last address anybody mentioned, and asking to start
   * again produced the same address back. Clearing the slate is a fact the app
   * knows and the model should not have to infer.
   */
  boundary?: boolean;
}

export interface IntakeData {
  sceneName: string;
  email: string;
  datasetLink: string;
  sceneType: string;
  industryUseCase: string;
  cameraType: string;
  gpsAvailable: boolean;
  advancedMetadata?: Record<string, unknown>;
  outputPreference?: { type: 'download' | 'viewer'; format?: string };
}

export interface WorldMapIntakeData {
  address: string;
  sceneName: string;
  email: string;
  /** Optional contact details, asked for alongside the required email. */
  fullName?: string;
  company?: string;
}

export interface VexcelCoverage {
  worldMapIntake: WorldMapIntakeData;
  /** What the geocoder actually matched — shown so a wrong match is obvious. */
  placeName?: string;
  /** Country of the match, so a coverage miss can say where. */
  country?: string;
  /** Unknown until the user picks an extent — coverage is validated on proceed. */
  year?: string;
  collection?: string;
  centerLat: number;
  centerLon: number;
  wkt: string;
  /** Radius the coverage step actually used, after server-side clamping. */
  radiusM?: number;
  /** Preview flow only — grants the image proxy this collection and extent. */
  imageToken?: string;
  /** Which views the crop actually has; a collection may lack some obliques. */
  imageViews?: string[];
}

export interface RecoSession {
  tab: 'images' | 'address' | null;
  setTab: React.Dispatch<React.SetStateAction<'images' | 'address' | null>>;
  radiusM: number;
  setRadiusM: React.Dispatch<React.SetStateAction<number>>;
  phase: Phase;
  messages: Message[];
  input: string;
  setInput: React.Dispatch<React.SetStateAction<string>>;
  loading: boolean;
  error: string;
  checkPassed: boolean | null;
  pendingWarning: { requestId: string } | null;
  warningActionLoading: boolean;
  vexcelCoverage: VexcelCoverage | null;
  setVexcelCoverage: React.Dispatch<React.SetStateAction<VexcelCoverage | null>>;
  vexcelProceedLoading: boolean;
  uploadFiles: File[];
  uploadedCount: number;
  uploadStatus: 'idle' | 'uploading' | 'done' | 'error';
  /* Owned here so resetSession and clearUpload can clear the field, and so the
     composer regains focus after a turn settles. Exactly one panel is mounted
     at a time, so there is never a contest for either node. */
  fileInputRef: React.RefObject<HTMLInputElement>;
  inputRef: React.RefObject<HTMLInputElement>;
  handleFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  clearUpload: () => void;
  /** Verify imagery before asking the user for anything. Locks the extent on success. */
  checkCoverage: () => Promise<void>;
  /** Preview flow: every sample view has loaded, so we can offer to proceed. */
  previewReady: boolean;
  /** Contact details asked for alongside the required email. */
  nameInput: string;
  setNameInput: React.Dispatch<React.SetStateAction<string>>;
  companyInput: string;
  setCompanyInput: React.Dispatch<React.SetStateAction<string>>;
  /** Which decision the reason buttons are asking about. */
  cancelKind: 'preview' | 'email';
  onPreviewReady: () => void;
  /** Preview flow: a view could not be fetched. 'forbidden' is entitlement. */
  onPreviewFailed: (kind: 'forbidden' | 'expired' | 'service') => void;
  /** Preview flow: accept the imagery and move on to the email. */
  acceptPreview: () => void;
  /** Preview flow: decline, then ask why. */
  declinePreview: () => void;
  /** Answer the "why?" with one of the fixed options and move on. */
  submitCancelReason: (reason: string) => void;
  /** Address typed into the dedicated field during 'email-capture'. */
  emailInput: string;
  setEmailInput: React.Dispatch<React.SetStateAction<string>>;
  emailValid: boolean;
  /** Why the last submit attempt was refused. Cleared as soon as they type. */
  emailError: string;
  /** Commit the reconstruction with the captured address. */
  submitEmail: () => Promise<void>;
  handleVexcelCancel: () => void;
  /** Coverage settled for the current extent — the map is frozen from here on. */
  coverageConfirmed: boolean;
  /** Re-offer the opening choices after a location we cannot serve. */
  showChoices: boolean;
  setShowChoices: React.Dispatch<React.SetStateAction<boolean>>;
  handleProceed: () => Promise<void>;
  handleRevise: () => Promise<void>;
  resetSession: () => void;
  chooseOption: (key: 'images' | 'address', message: string) => Promise<void>;
  send: () => Promise<void>;
  /** The user has taken a turn — the transcript is worth preserving. */
  conversationStarted: boolean;
  /** Nothing has happened yet, so a presentation may open in its resting state. */
  sessionFresh: boolean;
  /*
   * Which path reached 'done'. The upload path really is verifying a dataset;
   * the address path has nothing to verify — the imagery is ours to fetch — so
   * telling those users we are "checking your dataset" describes work that will
   * never happen.
   */
  doneKind: 'dataset' | 'worldmap';
}

const RecoSessionContext = createContext<RecoSession | null>(null);

export const RecoSessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useI18n();
  const ra = t.reconstructionAgent;

  const [tab, setTab]               = useState<'images' | 'address' | null>(null);
  const [radiusM, setRadiusM]       = useState<number>(RADIUS_DEFAULT_M);
  const [phase, setPhase]           = useState<Phase>('intake');
  const [messages, setMessages]     = useState<Message[]>([]);
  const [input, setInput]           = useState('');
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [checkPassed, setCheckPassed] = useState<boolean | null>(null);
  const [pendingWarning, setPendingWarning] = useState<{ requestId: string } | null>(null);
  const [warningActionLoading, setWarningActionLoading] = useState(false);
  const [vexcelCoverage, setVexcelCoverage] = useState<VexcelCoverage | null>(null);
  const [vexcelProceedLoading, setVexcelProceedLoading] = useState(false);
  /*
   * Set once the coverage step has succeeded for the extent currently on screen.
   * From that point the pin and the radius are frozen: moving them would mean the
   * verified rectangle and the reconstructed one are different rectangles, and
   * /proceed no longer re-checks. Choosing a different place is a new location
   * request, not an adjustment.
   */
  const [coverageConfirmed, setCoverageConfirmed] = useState(false);
  /* A location with no imagery ends the address path, so the opening choices
     come back — but `conversationStarted` is true by then, so they need their
     own trigger rather than the first-turn condition. */
  const [showChoices, setShowChoices] = useState(false);
  /* Kept apart from `input`: the composer is hidden during 'email-capture', and
     an address half-typed there should not survive into the next chat turn. */
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState('');
  const [previewReady, setPreviewReady] = useState(false);
  /*
   * Announcing the previews is once per grant, and `previewReady` alone cannot
   * enforce that: the callback below is handed to a panel that gets torn down
   * and rebuilt, so it must read the current value, not the one captured when it
   * was created. See `onPreviewReady` for what went wrong without this.
   */
  const previewAnnouncedRef = useRef(false);
  const [nameInput, setNameInput] = useState('');
  const [companyInput, setCompanyInput] = useState('');
  /*
   * Which decision the reason buttons are asking about. Declining the previews
   * and declining to give an email are different choices with different answers,
   * so they share the phase and the submit path but not the options.
   */
  const [cancelKind, setCancelKind] = useState<'preview' | 'email'>('preview');

  /*
   * Everything that belongs to one reconstruction attempt, in one place.
   *
   * Each exit used to clear its own hand-written list of four or five fields,
   * copy-pasted between them and already out of step: three of the four forgot
   * `previewReady`, and none cleared the email, name, company or radius — so a
   * cancelled attempt left its contact details sitting in the form for the next
   * one. Adding a field meant remembering five places, which is how `nameInput`,
   * `companyInput` and `cancelKind` came to be missing from `resetSession` too.
   *
   * Deliberately not the transcript: the record of what was tried and refused is
   * what the reader needs, and cancellations were asked to stay in the stream.
   * Deliberately not uploads either — they outlive a single attempt, and losing
   * someone's images because they abandoned an unrelated address would be a far
   * worse bug than a stale flag.
   */
  const resetFlowState = useCallback(() => {
    setVexcelCoverage(null);
    setCoverageConfirmed(false);
    setPreviewReady(false);
    previewAnnouncedRef.current = false;
    setTab(null);
    setRadiusM(RADIUS_DEFAULT_M);
    setEmailInput('');
    setNameInput('');
    setCompanyInput('');
    setEmailError('');
    setCancelKind('preview');
    setPendingWarning(null);
    setVexcelProceedLoading(false);
    setWarningActionLoading(false);
    setError('');
    setInput('');
  }, []);

  /*
   * Draw a line and re-offer the choices. Every exit that lands back at the
   * opening choices goes through here, so the boundary can never be forgotten at
   * one of them — which is exactly how the failed address survived.
   */
  const offerFreshStart = useCallback(() => {
    resetFlowState();
    setMessages((prev) => [...prev, { role: 'system', content: ra.newRequestDivider, boundary: true }]);
    setShowChoices(true);
    setPhase('intake');
  }, [resetFlowState, ra.newRequestDivider]);

  const [doneKind, setDoneKind] = useState<'dataset' | 'worldmap'>('dataset');

  // Direct upload state
  const [uploadFiles, setUploadFiles]         = useState<File[]>([]);
  const [uploadedCount, setUploadedCount]     = useState(0);
  const [uploadStatus, setUploadStatus]       = useState<'idle' | 'uploading' | 'done' | 'error'>('idle');
  const uploadRequestIdRef  = useRef<string | null>(null);
  const uploadPromiseRef    = useRef<Promise<void> | null>(null);
  const fileInputRef        = useRef<HTMLInputElement>(null);

  const intakeRef                   = useRef<IntakeData | null>(null);
  const pollTimerRef                = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollDeadlineRef             = useRef<number>(0);
  const pollPatienceShownRef        = useRef<boolean>(false);
  const progressCountRef            = useRef<number>(0);

  // The chat turn that carries the email resolves after the user has set an extent,
  // so the handler reads these rather than a captured render's state.
  const heldExtentRef = useRef<VexcelCoverage | null>(null);
  const radiusRef     = useRef<number>(RADIUS_DEFAULT_M);
  const inputRef  = useRef<HTMLInputElement>(null);

  /* Deliberately permissive — one @, something either side, a dot in the domain.
     The address is only ever used to send mail, so the real test is delivery;
     anything stricter rejects valid addresses for no gain. */
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailInput.trim());

  // The user has taken a turn, so there is a transcript worth keeping. Declared
  // ahead of the seeding effect below, which reads it.
  const conversationStarted = messages.some((m) => m.role === 'user');

  /*
   * Re-seed the greeting when the language changes — and only then. This effect
   * also runs on mount, which is how the opening message gets there at all, but
   * it used to be guarded on `phase === 'intake'` alone. Most of the intake Q&A
   * happens in that phase, so once one session outlives a mount, an unguarded
   * re-seed would wipe a live transcript on arrival. `conversationStarted` is
   * the condition the original comment described.
   */
  useEffect(() => {
    if (!conversationStarted && phase === 'intake') {
      setMessages([{ role: 'assistant', content: ra.welcome }]);
    }
  }, [ra.welcome]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { heldExtentRef.current = vexcelCoverage; }, [vexcelCoverage]);
  useEffect(() => { radiusRef.current = radiusM; }, [radiusM]);

  // Only on app teardown now. A panel unmounting mid-poll used to land here and
  // silently kill the status poll behind a navigation.
  useEffect(() => () => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
  }, []);

  // ── Direct upload ────────────────────────────────────────────────────────────
  const startDirectUpload = useCallback(async (files: File[]) => {
    const requestId = crypto.randomUUID();
    uploadRequestIdRef.current = requestId;
    setUploadedCount(0);
    setUploadStatus('uploading');

    const doUpload = async () => {
      const { credentials, bucket, region, prefix } = await customerApi.reconstructionUploadCredentials(requestId);

      const s3 = new S3Client({
        region,
        credentials: {
          accessKeyId: credentials.AccessKeyId,
          secretAccessKey: credentials.SecretAccessKey,
          sessionToken: credentials.SessionToken,
        },
      });

      let done = 0;
      for (let i = 0; i < files.length; i += UPLOAD_CONCURRENCY) {
        const batch = files.slice(i, i + UPLOAD_CONCURRENCY);
        await Promise.all(batch.map(async (file) => {
          const key = `${prefix}${file.webkitRelativePath || file.name}`;
          await s3.send(new PutObjectCommand({
            Bucket: bucket,
            Key: key,
            Body: file,
            ContentType: file.type || 'image/jpeg',
          }));
          done += 1;
          setUploadedCount(done);
        }));
      }
      setUploadStatus('done');
    };

    uploadPromiseRef.current = doUpload().catch((err) => {
      console.error('[ReconstructionAgent] upload error:', err);
      setUploadStatus('error');
      throw err;
    });
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const all = Array.from(e.target.files ?? []);
    const images = all.filter(f => ACCEPTED_EXTENSIONS.test(f.name));
    if (!images.length) return;

    setUploadFiles(images);

    // Inject a message into the intake conversation so Claude skips asking for a link
    const autoMsg = `I'm uploading ${images.length} images directly from my computer. [UPLOADING_DIRECTLY: ${images.length} files]`;
    // Injected so the model skips asking for a dataset link — it is intake
    // information in the user's voice, so it belongs in the dialogue.
    setMessages(prev => [...prev, { role: 'user', content: autoMsg, dialogue: true }]);

    startDirectUpload(images);

    // Clear the input so the user sees the auto-message was sent
    setInput('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [startDirectUpload]);

  const clearUpload = useCallback(() => {
    setUploadFiles([]);
    setUploadedCount(0);
    setUploadStatus('idle');
    uploadRequestIdRef.current = null;
    uploadPromiseRef.current = null;
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  // ── Phase 1: intake turn ────────────────────────────────────────────────────
  const sendIntake = useCallback(async (text: string) => {
    // Any turn from the user retires the re-offered choices, whether they picked
    // a card or simply typed the next address themselves.
    setShowChoices(false);

    const userMsg: Message = { role: 'user', content: text, dialogue: true };
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    setInput('');
    setLoading(true);
    setError('');

    try {
      const res = await customerApi.reconstructionAgentChat(
        nextMessages
          // Only this attempt: everything before the last boundary belongs to a
          // request that is finished, and re-reading it is how a failed address
          // came back as the answer to "start over".
          .slice(nextMessages.map((m) => !!m.boundary).lastIndexOf(true) + 1)
          // Dialogue only, in either voice. Empty replies go too: they carry
          // nothing, and an empty turn is not something to ask the upstream API
          // to read.
          .filter((m) => m.dialogue && m.content.trim() !== '')
          .map(({ role, content }) => ({
            role: role as 'user' | 'assistant',
            content,
          })),
      );

      const assistantMsg: Message = { role: 'assistant', content: res.reply, dialogue: true };
      setMessages((prev) => [...prev, assistantMsg]);

      if (res.intake) {
        intakeRef.current = res.intake;
        // If a direct upload is in progress, wait for it before submitting
        if (uploadPromiseRef.current && uploadStatus !== 'done') {
          setMessages((prev) => [...prev, {
            role: 'assistant',
            content: 'Almost there — just finishing your upload before we kick things off.',
          }]);
          try {
            await uploadPromiseRef.current;
          } catch {
            setError('Upload failed. Please check your files and try again.');
            setPhase('intake');
            setLoading(false);
            return;
          }
        }
        setTimeout(() => submitReconstruction(res.intake!, uploadRequestIdRef.current ?? undefined), 100);
      } else if ((res as any).worldMapAddress) {
        // Address captured — geocode and show the map straight away. Email comes
        // later, so nothing gates the user from setting their area first.
        const wma = (res as any).worldMapAddress as { address: string; sceneName: string };
        setTimeout(() => openExtentPicker({ address: wma.address, sceneName: wma.sceneName, email: '' }), 100);
      } else if ((res as any).worldMapIntake) {
        // Email now in hand — submit using the extent they already chose.
        const wmi = (res as any).worldMapIntake as WorldMapIntakeData;
        setTimeout(() => {
          if (heldExtentRef.current) submitWorldMap(wmi, heldExtentRef.current, radiusRef.current);
          else openExtentPicker(wmi);
        }, 100);
      }
    } catch {
      setError(ra.errorRetry);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [messages, ra.errorRetry]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── World Map: check Vexcel coverage, then show confirm card ────────────────
  const openExtentPicker = useCallback(async (worldMapIntake: WorldMapIntakeData) => {
    /*
     * A new location starts clean. Without this, coverageConfirmed survived from
     * the previous run and the fresh extent card mounted read-only with its
     * Check coverage button disabled — a map you could see but not touch. It
     * only showed up after a decline, because that is the one exit that leaves
     * a *confirmed* coverage behind.
     */
    setCoverageConfirmed(false);
    setPreviewReady(false);
    previewAnnouncedRef.current = false;
    setPhase('connecting');
    setMessages((prev) => [...prev, { role: 'assistant', content: ra.locatingAddress }]);
    try {
      const res = await customerApi.reconstructionWorldMapSubmit(worldMapIntake);
      setMessages((prev) => [...prev, { role: 'assistant', content: res.message }]);
      if (res.success && res.centerLat != null && res.centerLon != null) {
        setRadiusM(res.radiusM ?? RADIUS_DEFAULT_M);
        setVexcelCoverage({
          worldMapIntake,
          placeName: res.placeName,
          country: res.country,
          centerLat: res.centerLat,
          centerLon: res.centerLon,
          wkt: res.wkt!,
        });
        setPhase('vexcel-confirm');
      } else {
        setPhase('intake');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : ra.errorRetry);
      setPhase('intake');
    }
  }, [ra.errorRetry, ra.locatingAddress]);

  // ── World Map: user confirms — kick off async reconstruction ─────────────────
  const submitWorldMap = useCallback(async (
    intake: WorldMapIntakeData,
    held: VexcelCoverage,
    radius: number,
  ) => {
    setVexcelProceedLoading(true);
    setError('');
    try {
      const res = await customerApi.reconstructionWorldMapProceed({
        worldMapIntake: intake,
        centerLat: held.centerLat,
        centerLon: held.centerLon,
        // The radius the coverage step settled on after clamping, not the raw
        // slider value — so /proceed rebuilds the rectangle that was verified.
        radiusM: held.radiusM ?? radius,
        collection: held.collection,
        year: held.year,
      });
      setMessages((prev) => [...prev, { role: 'assistant', content: res.message }]);
      if (!res.success) {
        // No imagery for the chosen extent — keep the map up so they can adjust it.
        return;
      }
      setVexcelCoverage(null);
      setCheckPassed(true);
      setDoneKind('worldmap');
      setPhase('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : ra.errorRetry);
    } finally {
      setVexcelProceedLoading(false);
    }
  }, [ra.errorRetry]);

  /*
   * The coverage step. This is what the button on the extent card now does, in
   * place of starting a reconstruction outright.
   *
   * On success the extent is frozen and the collection kept, because /proceed no
   * longer re-verifies — it reconstructs whatever this call approved. Only then
   * is the email asked for, so nobody hands one over for a location we cannot
   * serve. On failure the address path ends and the opening choices return:
   * nudging a box we have no imagery for is a dead end, and picking a different
   * place is a new location request rather than an adjustment.
   */
  const checkCoverage = useCallback(async () => {
    if (!vexcelCoverage) return;
    setVexcelProceedLoading(true);
    setError('');
    try {
      const res = await customerApi.reconstructionWorldMapCoverage({
        centerLat: vexcelCoverage.centerLat,
        centerLon: vexcelCoverage.centerLon,
        radiusM,
        country: vexcelCoverage.country,
      });
      if (!res.success) {
        /*
         * The extent card is about to unmount, so the area the user framed goes
         * into the transcript first. Without it the map simply vanishes and the
         * refusal has nothing to point at — they are left rereading an address
         * to work out which box we turned down.
         */
        setMessages((prev) => [
          ...prev,
          {
            role: 'user',
            content: ra.extentSelected,
            extent: {
              lat: vexcelCoverage.centerLat,
              lon: vexcelCoverage.centerLon,
              radiusM: res.radiusM ?? radiusM,
              address: vexcelCoverage.placeName ?? vexcelCoverage.worldMapIntake.address,
            },
          },
          { role: 'assistant', content: `${res.message} ${ra.startOver}` },
        ]);
        offerFreshStart();
        return;
      }

      // The radius the server actually used after clamping, so /proceed rebuilds
      // the rectangle that was verified rather than the one the slider showed.
      const settledRadius = res.radiusM ?? radiusM;
      const settled = {
        lat: vexcelCoverage.centerLat,
        lon: vexcelCoverage.centerLon,
        radiusM: settledRadius,
        address: vexcelCoverage.placeName ?? vexcelCoverage.worldMapIntake.address,
      };
      const confirmed: VexcelCoverage = {
        ...vexcelCoverage,
        collection: res.collection,
        year: res.year,
        radiusM: settledRadius,
        imageToken: res.imageToken,
        imageViews: res.views,
      };
      setVexcelCoverage(confirmed);
      setCoverageConfirmed(true);

      // The area first, then the verdict on it. An extent rides along with a
      // message and renders below the bubble, so carrying it on the verdict
      // would put the words above the thing they describe — it goes on a
      // contentless message of its own instead.
      setMessages((prev) => [
        ...prev,
        { role: 'user', content: ra.extentSelected, extent: settled },
        { role: 'assistant', content: res.message },
      ]);

      /*
       * Preview flow. The server has already established coverage *and* that all
       * five views exist — both cheap, both before this point — so the bubble
       * above is safe to say. What remains is fetching the pixels, which the
       * browser does, and which is the only place an entitlement failure shows
       * up. Hence a second bubble that names what is still happening.
       */
      if (res.mode === 'preview' && res.imageToken) {
        setPreviewReady(false);
        previewAnnouncedRef.current = false;
        setPhase('imagery-preview');
        setMessages((prev) => [...prev, { role: 'assistant', content: ra.previewVerifying }]);
        return;
      }

      // An email already in hand (a second run in one session) leaves nothing to ask.
      if (confirmed.worldMapIntake.email) {
        await submitWorldMap(confirmed.worldMapIntake, confirmed, settledRadius);
        return;
      }

      // Its own phase, so the composer stands down and the field owns the turn —
      // two places to type an address is the confusion this avoids.
      setPhase('email-capture');
      setMessages((prev) => [...prev, { role: 'assistant', content: ra.coverageEmailAsk }]);
    } catch (err) {
      setError(err instanceof Error ? err.message : ra.errorRetry);
    } finally {
      setVexcelProceedLoading(false);
    }
  }, [vexcelCoverage, radiusM, offerFreshStart, submitWorldMap, ra.coverageEmailAsk, ra.previewVerifying, ra.startOver, ra.extentSelected, ra.errorRetry]);

  /*
   * Commit with the address from the dedicated field. This goes straight to
   * /proceed rather than through the chat: the model has nothing to add to an
   * email address, and routing it through an intake turn would spend an
   * Anthropic call to learn what the field already knows.
   */
  const setEmailInputAndClear = useCallback<React.Dispatch<React.SetStateAction<string>>>((v) => {
    setEmailInput(v);
    setEmailError('');
  }, []);

  const submitEmail = useCallback(async () => {
    const email = emailInput.trim();
    if (!vexcelCoverage) return;
    /* The button stays live rather than going disabled on an empty field: a dead
       button explains nothing, and the commonest reason for pressing it is not
       having noticed the field. Say what is wrong instead. */
    if (!email) { setEmailError(ra.emailRequired); return; }
    if (!emailValid) { setEmailError(ra.emailInvalid); return; }
    setEmailError('');
    const fullName = nameInput.trim();
    const company = companyInput.trim();
    const withEmail: VexcelCoverage = {
      ...vexcelCoverage,
      worldMapIntake: {
        ...vexcelCoverage.worldMapIntake,
        email,
        ...(fullName ? { fullName } : {}),
        ...(company ? { company } : {}),
      },
    };
    setVexcelCoverage(withEmail);
    setEmailInput('');
    setNameInput('');
    setCompanyInput('');
    // Said back the way they gave it — the optional fields only when offered, so
    // an empty one leaves no trace in the record.
    const said = [fullName, company, email].filter(Boolean).join(' \u00b7 ');
    setMessages((prev) => [...prev, { role: 'user', content: said }]);
    await submitWorldMap(withEmail.worldMapIntake, withEmail, withEmail.radiusM ?? radiusM);
  }, [emailInput, nameInput, companyInput, emailValid, vexcelCoverage, radiusM, submitWorldMap,
      ra.emailRequired, ra.emailInvalid]);

  /** Every view arrived — announce it, then offer the decision. */
  const onPreviewReady = useCallback(() => {
    /*
     * Once per grant, however many times the panel reports itself ready.
     *
     * It reports on mount, after its five fetches land — and it is mounted
     * inside the composer, which `pendingAction` unmounts whenever the
     * transcript is mid-reveal. So appending this very message unmounted the
     * panel, the reveal settled, the panel remounted, refetched all five views
     * and announced itself again: a bubble and five image fetches per turn of
     * the loop, forever. The panel is right to report each time it loads; it is
     * this side that must only say it once.
     */
    if (previewAnnouncedRef.current) return;
    previewAnnouncedRef.current = true;
    setPreviewReady(true);
    setMessages((prev) => [...prev, { role: 'assistant', content: ra.previewReadyMsg }]);
  }, [ra.previewReadyMsg]);

  /*
   * A view could not be fetched. 403 means the imagery exists and we are not
   * entitled to it, which is a different sentence from an outage — the legacy
   * path could not tell these apart and said "no coverage" for both.
   */
  const onPreviewFailed = useCallback((kind: 'forbidden' | 'expired' | 'service') => {
    const held = heldExtentRef.current;
    setMessages((prev) => [
      ...prev,
      // Same reasoning as the coverage refusal: keep the framed area in view.
      ...(held ? [{
        role: 'user' as const,
        content: ra.extentSelected,
        extent: {
          lat: held.centerLat,
          lon: held.centerLon,
          radiusM: held.radiusM ?? radiusM,
          address: held.placeName ?? held.worldMapIntake.address,
        },
      }] : []),
      {
        role: 'assistant' as const,
        content: `${
          kind === 'forbidden' ? ra.previewForbidden
            : kind === 'expired' ? ra.previewExpired
            : ra.previewUnsuitable
        } ${ra.startOver}`,
      },
    ]);
    offerFreshStart();
  }, [radiusM, offerFreshStart, ra.previewForbidden, ra.previewExpired, ra.previewUnsuitable, ra.startOver, ra.extentSelected]);

  const acceptPreview = useCallback(() => {
    const token = heldExtentRef.current?.imageToken;
    setPhase('email-capture');
    setMessages((prev) => [
      ...prev,
      { role: 'user', content: ra.previewAccepted, imageToken: token, imageViews: heldExtentRef.current?.imageViews },
      { role: 'assistant', content: ra.coverageEmailAsk },
    ]);
  }, [ra.coverageEmailAsk, ra.previewAccepted]);

  /*
   * Declining asks why. The answer arrives as the next chat turn, so a flag
   * routes that one message to the cancel log instead of the intake model —
   * otherwise "the imagery looks too old" would be read as a new request.
   */
  const declinePreview = useCallback(() => {
    const held = heldExtentRef.current;
    setPreviewReady(false);
    previewAnnouncedRef.current = false;
    /*
     * The imagery moves into the transcript on the way out. Cancelling should
     * not also mean losing sight of what was cancelled — the next question is
     * "why?", which is hard to answer about something no longer on screen.
     * "Cancelled" goes underneath rather than above, so the record reads as the
     * thing and then the verdict on it.
     */
    setMessages((prev) => [
      ...prev,
      ...(held?.imageToken ? [{
        role: 'user' as const,
        content: '',
        imageToken: held.imageToken,
        imageViews: held.imageViews,
      }] : []),
      { role: 'user' as const, content: ra.cancelled },
      { role: 'assistant' as const, content: ra.previewWhyCancel },
    ]);
    setCancelKind('preview');
    setPhase('cancel-reason');
  }, [ra.previewWhyCancel, ra.cancelled]);

  const submitCancelReason = useCallback((reason: string) => {
    const held = heldExtentRef.current;
    customerApi.reconstructionWorldMapCancel({
      reason,
      address: held?.worldMapIntake.address,
      collection: held?.collection,
    }).catch(() => {});
    setMessages((prev) => [
      ...prev,
      { role: 'user', content: reason },
      { role: 'assistant', content: ra.previewCancelThanks },
    ]);
    offerFreshStart();
  }, [offerFreshStart, ra.previewCancelThanks]);

  const handleVexcelCancel = useCallback(() => {
    const held = heldExtentRef.current;
    /*
     * Only the extent card has anything unsaved: coverage puts the map into the
     * transcript itself, and accepting the previews puts those there too. So
     * this preserves the map only when cancelling before that point, and never
     * duplicates one already in the record.
     */
    const keepExtent = phase === 'vexcel-confirm' && !coverageConfirmed && !!held;
    /*
     * At the email step the map and the previews are already in the transcript,
     * so all that is missing is what the user actually decided — and there it is
     * a specific decision, not a generic one. Saying it in their own words keeps
     * the record readable months later.
     */
    setMessages((prev) => [
      ...prev,
      ...(keepExtent ? [{
        role: 'user' as const,
        content: '',
        extent: {
          lat: held!.centerLat,
          lon: held!.centerLon,
          radiusM: held!.radiusM ?? radiusM,
          address: held!.placeName ?? held!.worldMapIntake.address,
        },
      }] : []),
      { role: 'user' as const, content: phase === 'email-capture' ? ra.cancelledNoEmail : ra.cancelled },
      {
        role: 'assistant' as const,
        content: phase === 'email-capture'
          ? ra.emailWhyCancel
          : "No problem — let me know if you'd like to try a different address or use your own dataset.",
      },
    ]);
    /*
     * Backing out at the email step is worth a reason: the imagery passed and the
     * area was approved, so whatever stopped them is about us rather than about
     * the location. Everywhere else the exit is self-explanatory and asking would
     * just be another click.
     */
    if (phase === 'email-capture') {
      setCancelKind('email');
      setPhase('cancel-reason');
      return;
    }
    offerFreshStart();
  }, [phase, coverageConfirmed, radiusM, offerFreshStart, ra.cancelled, ra.cancelledNoEmail, ra.emailWhyCancel]);

  // ── Poll for check result ────────────────────────────────────────────────────
  const pollStatus = useCallback((requestId: string) => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    const now = Date.now();
    if (now > pollDeadlineRef.current) {
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: "The dataset check is taking longer than expected — our team has been notified and will follow up by email. You're all set to close this.",
      }]);
      setCheckPassed(false);
      setPhase('done');
      return;
    }
    // After 90s with no progress messages yet, show a one-time patience note
    if (!pollPatienceShownRef.current && progressCountRef.current === 0 && now > pollDeadlineRef.current - (10 * 60_000 - 90_000)) {
      pollPatienceShownRef.current = true;
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: "I'm downloading a sample of your images and checking them for quality, EXIF data, and focal length consistency. This usually takes 3–5 minutes — thanks for your patience!",
      }]);
    }
    pollTimerRef.current = setTimeout(async () => {
      try {
        const res = await customerApi.reconstructionStatus(requestId);
        if (res.pending) {
          // Append any new progress messages from the agent (deduplicate by count)
          const newMsgs = (res.progressMessages ?? []).slice(progressCountRef.current);
          if (newMsgs.length > 0) {
            progressCountRef.current += newMsgs.length;
            setMessages((prev) => [
              ...prev,
              ...newMsgs.map((content) => ({ role: 'assistant' as const, content })),
            ]);
          }
          pollStatus(requestId);
        } else if (res.requestStatus === 'sample_warning') {
          // Warning — show message + action buttons, stay in connecting phase
          setPendingWarning({ requestId });
          setMessages((prev) => [...prev, { role: 'assistant', content: res.message ?? '' }]);
          setPhase('connecting');
        } else {
          const passed = res.requestStatus === 'queued_for_processing';
          setCheckPassed(passed);
          setMessages((prev) => [...prev, { role: 'assistant', content: res.message ?? '' }]);
          setPhase(passed ? 'done' : 'intake');
        }
      } catch {
        pollStatus(requestId); // retry on transient error
      }
    }, 4000);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Warning action handlers ───────────────────────────────────────────────────
  const handleProceed = useCallback(async () => {
    if (!pendingWarning) return;
    // Dismiss buttons and transition to done immediately — Phase 3 runs in the background
    const requestId = pendingWarning.requestId;
    setPendingWarning(null);
    setCheckPassed(true);
    setMessages((prev) => [...prev, { role: 'assistant', content: "Got it. We'll proceed with this dataset as-is and queue it for processing. We'll notify you at the email you provided when it's ready." }]);
    setPhase('done');
    // Fire in background — don't block the UI
    customerApi.reconstructionProceed(requestId).catch(() => {});
  }, [pendingWarning]);

  const handleRevise = useCallback(async () => {
    if (!pendingWarning) return;
    setWarningActionLoading(true);
    setError('');
    try {
      const res = await customerApi.reconstructionRevise(pendingWarning.requestId);
      setPendingWarning(null);
      setMessages((prev) => [...prev, { role: 'assistant', content: res.message }]);
      setPhase('intake');
    } catch (err) {
      setError(err instanceof Error ? err.message : ra.errorRetry);
    } finally {
      setWarningActionLoading(false);
    }
  }, [pendingWarning, ra.errorRetry]);

  // ── Submit intake to backend — triggers async dataset verification ───────────
  const submitReconstruction = useCallback(async (intake: IntakeData, uploadRequestId?: string) => {
    setPhase('connecting');
    pollPatienceShownRef.current = false;
    progressCountRef.current = 0;
    setMessages((prev) => [...prev, { role: 'system', content: ra.systemChecking }]);

    try {
      const res = await customerApi.reconstructionSubmit(intake, uploadRequestId);
      setMessages((prev) => [...prev, { role: 'assistant', content: res.message }]);
      if (res.phase1Failed) {
        // Dataset wasn't accessible — show message and let user correct & resubmit
        setCheckPassed(false);
        setPhase('intake');
      } else {
        // Phase 1 passed — poll for Phase 2 result (agent sampling, takes a few minutes)
        pollDeadlineRef.current = Date.now() + 10 * 60_000;
        pollPatienceShownRef.current = false;
        pollStatus(res.requestId);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : ra.errorRetry);
      setPhase('intake');
    }
  }, [ra.systemChecking, ra.errorRetry, pollStatus]);

  // ── Phase 2: SI relay turn ──────────────────────────────────────────────────
  const sendRelay = useCallback(async (text: string) => {
    const userMsg: Message = { role: 'user', content: text };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    setError('');

    try {
      const res = await customerApi.reconstructionRelay(text);

      const siMsg: Message = {
        role: 'assistant',
        content: res.reply,
        files: res.files,
      };
      setMessages((prev) => [...prev, siMsg]);

      if (res.done) setPhase('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : ra.errorRetry);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [ra.errorRetry]);

  // Opening choice — records the path, then tells the model in the user's own voice
  // Full reset — the only way back to the very start once a path is under way.
  const resetSession = useCallback(() => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    resetFlowState();
    setPhase('intake');
    setMessages([{ role: 'assistant', content: ra.welcome }]);
    setLoading(false);
    setCheckPassed(null);
    setShowChoices(false);
    setDoneKind('dataset');
    // The transcript and the upload really do go here — this is the one reset
    // that ends the session rather than an attempt within it.
    setUploadFiles([]);
    setUploadedCount(0);
    setUploadStatus('idle');
    uploadRequestIdRef.current = null;
    uploadPromiseRef.current = null;
    if (fileInputRef.current) fileInputRef.current.value = '';
    intakeRef.current = null;
    pollDeadlineRef.current = 0;
    pollPatienceShownRef.current = false;
    progressCountRef.current = 0;
  }, [resetFlowState, ra.welcome]);

  const chooseOption = useCallback(async (key: 'images' | 'address', message: string) => {
    if (loading) return;
    setShowChoices(false);
    setTab(key);
    await sendIntake(message);
  }, [loading, sendIntake]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || loading || phase === 'connecting' || phase === 'vexcel-confirm'
      || phase === 'imagery-preview' || phase === 'email-capture') return;
    /* After 'done' the conversation goes back to Reco, not to the SI relay: the
       relay talks to a running reconstruction, and a job just queued has none. A
       follow-up here is a fresh request — another address, or a question. */
    if (phase === 'intake' || phase === 'done') {
      await sendIntake(text);
    } else {
      await sendRelay(text);
    }
  }, [input, loading, phase, sendIntake, sendRelay]);


  const sessionFresh =
    messages.length <= 1 && phase === 'intake' && tab === null && !input.trim() && !loading;

  const value = useMemo<RecoSession>(() => ({
    tab, setTab, radiusM, setRadiusM, phase, messages, input, setInput, loading, error,
    checkPassed, pendingWarning, warningActionLoading, vexcelCoverage, setVexcelCoverage,
    vexcelProceedLoading, uploadFiles, uploadedCount, uploadStatus,
    fileInputRef, inputRef,
    handleFileSelect, clearUpload, checkCoverage, handleVexcelCancel,
    previewReady, onPreviewReady, onPreviewFailed, acceptPreview, declinePreview, submitCancelReason,
    nameInput, setNameInput, companyInput, setCompanyInput, cancelKind,
    emailInput, setEmailInput: setEmailInputAndClear, emailValid, emailError, submitEmail,
    coverageConfirmed, showChoices, setShowChoices,
    handleProceed, handleRevise, resetSession, chooseOption, send,
    conversationStarted, sessionFresh, doneKind,
  }), [
    tab, radiusM, phase, messages, input, loading, error, checkPassed, pendingWarning,
    warningActionLoading, vexcelCoverage, vexcelProceedLoading, uploadFiles, uploadedCount,
    uploadStatus, handleFileSelect, clearUpload, checkCoverage, handleVexcelCancel,
    previewReady, onPreviewReady, onPreviewFailed, acceptPreview, declinePreview, submitCancelReason,
    nameInput, setNameInput, companyInput, setCompanyInput, cancelKind,
    emailInput, setEmailInputAndClear, emailValid, emailError, submitEmail,
    coverageConfirmed, showChoices, setShowChoices,
    handleProceed, handleRevise, resetSession, chooseOption, send,
    conversationStarted, sessionFresh, doneKind,
  ]);

  return <RecoSessionContext.Provider value={value}>{children}</RecoSessionContext.Provider>;
};

export function useRecoSession(): RecoSession {
  const ctx = useContext(RecoSessionContext);
  if (!ctx) throw new Error('useRecoSession must be used within a RecoSessionProvider');
  return ctx;
}
